import React from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlements } from '../../hooks/useEntitlements';

/**
 * Shown once a Free workspace has spent its daily MCP data-call quota
 * (PRICING_V3_SPEC.md §7). The wall is hit inside ChatGPT/Claude, so this is
 * usually the user's first in-product explanation of why their AI stopped
 * answering — it has to name the cause and offer the fix in one line.
 *
 * Not dismissible: the condition clears itself at the reset, and a dismissed
 * banner would leave the user with no explanation for a still-broken AI.
 */
const McpQuotaBanner: React.FC = () => {
  // Mounted above the router so no page layout can hide it, which also means it
  // renders on the signed-out auth pages — gate the entitlements fetch on auth
  // or every visitor to /login fires a 401. Same guard as FeedbackWidget.
  const { isAuthenticated } = useAuth();
  const { data } = useEntitlements({ enabled: isAuthenticated });
  const quota = data?.mcpQuota;

  if (!isAuthenticated || !quota || quota.used < quota.limit) return null;

  const resetsAt = new Date(quota.periodEndsAt);
  const resetLabel = Number.isNaN(resetsAt.getTime())
    ? null
    : resetsAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-warn/40 bg-warn/15 px-4 py-2 text-[13px] text-foreground">
      <Zap className="h-4 w-4 flex-none text-warn" />
      <span className="min-w-0">
        Daily AI query limit reached
        <span className="text-muted-foreground">
          {' '}· {quota.used} of {quota.limit} used
          {resetLabel ? ` · resets ${resetLabel}` : ''}
        </span>
      </span>
      <Link
        to="/settings/billing"
        className="ml-auto rounded-md bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground hover:opacity-90"
      >
        Upgrade plan
      </Link>
    </div>
  );
};

export default McpQuotaBanner;
